/**
 * Table with configurable columns and load-more.
 * Visible rows are driven by parent (limit/load-more); columns use valueKey to read from ReportWidgetItem.
 */
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { memo, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ReportWidgetStickyHeader } from './table/ReportWidgetStickyHeader';
import { ReportWidgetTableCell } from './table/ReportWidgetTableCell';
import { ReportWidgetTableHeaderCell } from './table/ReportWidgetTableHeaderCell';
import { ReportWidgetTableLoadMore } from './table/ReportWidgetTableLoadMore';
import { ReportWidgetTableLoading } from './table/ReportWidgetTableLoading';

import { useHorizontalTableScroll } from '../model/hooks/useHorizontalTableScroll';
import { useReportWidgetStickyHeader } from '../model/hooks/useReportWidgetStickyHeader';
import {
    type ReportWidgetColumnConfig,
    type ReportWidgetItem,
    type ReportWidgetTableOptions,
    getChartItemId,
} from '../model/types';

function getColumnFallbackMinWidth(col: ReportWidgetColumnConfig): number {
    if (col.format === 'currency') return 112;
    if (col.format === 'number') return 84;
    if (col.format === 'percent') return 84;
    return 96;
}

interface ReportWidgetTableProps {
    items: ReportWidgetItem[];
    columns: ReportWidgetColumnConfig[];
    /** Presentation options (labels, show details column). Aligns with config + derived state. */
    options?: ReportWidgetTableOptions;
    /** Resolved colors per row (same order as items), for swatch in label column. */
    itemColors?: string[];
    loading?: boolean;
    loadingMore?: boolean;
    hasMore?: boolean;
    activeItemId?: string;
    onLoadMore?: () => void;
    onRowClick?: (item: ReportWidgetItem) => void;
    onRowHover?: (item: ReportWidgetItem | null) => void;
    onDetailsClick?: (item: ReportWidgetItem) => void;
    onLoadMoreHover?: (hovering: boolean) => void;
    canCollapse?: boolean;
    onCollapse?: () => void;
}

export const ReportWidgetTable = memo(
    ({
        items,
        columns,
        options,
        itemColors = [],
        loading = false,
        loadingMore = false,
        hasMore = false,
        activeItemId,
        onLoadMore,
        onRowClick,
        onRowHover,
        onDetailsClick,
        onLoadMoreHover,
        canCollapse,
        onCollapse,
    }: ReportWidgetTableProps) => {
        const { t } = useTranslation();
        const {
            containerRef: tableContainerRef,
            hasHorizontalOverflow,
            isAtScrollStart,
            isAtScrollEnd,
            handleScroll: updateHorizontalOverflowState,
            refresh: refreshHorizontalOverflowState,
        } = useHorizontalTableScroll();
        const {
            loadMoreLabel,
            loadMoreVariant = 'button',
            loadMoreCount = 0,
            showColorSwatchInLabelColumn,
            emptyMessage,
        } = options ?? {};
        const tableMinWidth = useMemo(
            () =>
                columns.reduce(
                    (sum, col) =>
                        sum + (col.minWidth ?? getColumnFallbackMinWidth(col)),
                    0,
                ),
            [columns],
        );
        const hasRowInteraction = Boolean(onRowClick || onRowHover);

        useEffect(() => {
            refreshHorizontalOverflowState();
        }, [items, columns, refreshHorizontalOverflowState]);

        const {
            bodyTableRef,
            stickyHeaderInnerRef,
            headerColumnWidths,
            headerTableWidth,
            headerHeight,
            handleTableScroll,
        } = useReportWidgetStickyHeader({
            columns,
            itemsLength: items.length,
            tableMinWidth,
            scrollContainerRef: tableContainerRef,
            onTableScrolled: updateHorizontalOverflowState,
        });

        if (loading) {
            return <ReportWidgetTableLoading />;
        }

        return (
            <Box
                data-testclass="composable-report-widget-table"
                sx={{ width: '100%', minWidth: 0 }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        width: '100%',
                        minWidth: 0,
                    }}
                >
                    <ReportWidgetStickyHeader
                        headerHeight={headerHeight}
                        headerTableWidth={headerTableWidth}
                        tableMinWidth={tableMinWidth}
                        stickyHeaderInnerRef={stickyHeaderInnerRef}
                    >
                        {columns.map((col, index) => (
                            <ReportWidgetTableHeaderCell
                                key={col.id}
                                column={col}
                                measuredWidth={headerColumnWidths[index]}
                            />
                        ))}
                    </ReportWidgetStickyHeader>
                    <TableContainer
                        ref={tableContainerRef}
                        onScroll={handleTableScroll}
                        sx={{
                            width: '100%',
                            maxWidth: '100%',
                            overflowX: 'auto',
                            overflowY: 'visible',
                        }}
                    >
                        <Table
                            ref={bodyTableRef}
                            size="medium"
                            sx={{
                                width: '100%',
                                minWidth: `${tableMinWidth}px`,
                            }}
                        >
                            <TableHead
                                sx={{
                                    '& .MuiTableCell-head': {
                                        backgroundColor: 'transparent',
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                    },
                                }}
                            >
                                <TableRow>
                                    {columns.map((col) => (
                                        <ReportWidgetTableHeaderCell
                                            key={col.id}
                                            column={col}
                                        />
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {items.length === 0 ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={columns.length}
                                            sx={{
                                                textAlign: 'center',
                                                color: 'text.secondary',
                                                padding: 3,
                                            }}
                                        >
                                            <Typography variant="body2">
                                                {emptyMessage ??
                                                    t(
                                                        'composableReportWidget.noData',
                                                    )}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    items.map((item, rowIndex) => (
                                        <TableRow
                                            key={item.id}
                                            hover={hasRowInteraction}
                                            onClick={() => onRowClick?.(item)}
                                            onMouseEnter={() =>
                                                onRowHover?.(item)
                                            }
                                            onMouseLeave={() =>
                                                onRowHover?.(null)
                                            }
                                            sx={{
                                                cursor: hasRowInteraction
                                                    ? 'pointer'
                                                    : 'default',
                                                borderBottom: '1px solid',
                                                borderColor: 'divider',
                                                backgroundColor:
                                                    activeItemId &&
                                                    getChartItemId(item) ===
                                                        activeItemId
                                                        ? 'action.hover'
                                                        : undefined,
                                                transition:
                                                    'background-color 120ms ease-out',

                                                '&:hover .details-icon': {
                                                    opacity: 1,
                                                },
                                            }}
                                        >
                                            {columns.map((col) => (
                                                <ReportWidgetTableCell
                                                    key={col.id}
                                                    item={item}
                                                    column={col}
                                                    showColorSwatchInLabelColumn={
                                                        showColorSwatchInLabelColumn
                                                    }
                                                    swatchColor={
                                                        itemColors[rowIndex] ??
                                                        item.color
                                                    }
                                                    onLabelClick={
                                                        col.isDetailsTrigger &&
                                                        onDetailsClick
                                                            ? () =>
                                                                  onDetailsClick(
                                                                      item,
                                                                  )
                                                            : undefined
                                                    }
                                                />
                                            ))}
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {hasHorizontalOverflow && !isAtScrollStart && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                bottom: 0,
                                left: 0,
                                width: 32,
                                pointerEvents: 'none',
                                background:
                                    'linear-gradient(to right, rgba(255,255,255,0.98), rgba(255,255,255,0))',
                            }}
                        />
                    )}
                    {hasHorizontalOverflow && !isAtScrollEnd && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                bottom: 0,
                                right: 0,
                                width: 32,
                                pointerEvents: 'none',
                                background:
                                    'linear-gradient(to left, rgba(255,255,255,0.98), rgba(255,255,255,0))',
                            }}
                        />
                    )}
                </Box>
                {(hasMore || loadingMore || canCollapse) &&
                    (onLoadMore || onCollapse) && (
                        <ReportWidgetTableLoadMore
                            loadMoreVariant={loadMoreVariant}
                            loadingMore={loadingMore}
                            onLoadMore={onLoadMore}
                            onLoadMoreHover={onLoadMoreHover}
                            onCollapse={onCollapse}
                            loadMoreCount={loadMoreCount}
                            loadMoreLabel={loadMoreLabel}
                            isFullyExpanded={Boolean(canCollapse && !hasMore)}
                        />
                    )}
            </Box>
        );
    },
);

ReportWidgetTable.displayName = 'ReportWidgetTable';
